import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { getImpersonationSession } from "@/features/impersonation/impersonation-session";
import type { User } from "@/common/types/user";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { OrganizationTermsAgreement } from "./organization-terms-agreement";
import { useOrganizationTerms } from "./use-organization-terms";

type OrganizationTermsCardProps = {
  organizationId: string;
  user?: Pick<User, "is_admin">;
  onContinue?: () => void;
};

export function OrganizationTermsCard({
  organizationId,
  user,
  onContinue,
}: OrganizationTermsCardProps) {
  const termsQuery = useOrganizationTerms({ organizationId });
  // An impersonation session only starts or ends with a full page load.
  const [impersonation] = useState(getImpersonationSession);
  // Members wait here until their administrator accepts, but Saile staff never
  // sign for a customer, so staff and impersonating staff continue regardless.
  const canContinue =
    Boolean(termsQuery.data?.acceptance) ||
    Boolean(user?.is_admin) ||
    impersonation !== null;

  useEffect(() => {
    if (
      organizationId &&
      termsQuery.isSuccess &&
      !termsQuery.isFetching &&
      termsQuery.data?.agreement === null
    ) {
      onContinue?.();
    }
  }, [
    organizationId,
    termsQuery.isSuccess,
    termsQuery.isFetching,
    termsQuery.data?.agreement,
    onContinue,
  ]);

  if (
    !organizationId ||
    (termsQuery.isSuccess && termsQuery.data?.agreement === null)
  ) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization Terms of Service</CardTitle>
        {termsQuery.data && (
          <CardDescription>
            Review the terms for {termsQuery.data.organizationName}. One
            organization administrator can accept on behalf of the organization.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {termsQuery.isPending && (
          <p role="status" className="text-sm text-muted-foreground">
            Loading organization terms…
          </p>
        )}
        {termsQuery.isError && (
          <div className="space-y-3">
            <p role="alert" className="text-sm text-destructive">
              We couldn't load your organization's terms. Please try again.
            </p>
            <Button
              variant="outline"
              onClick={() => void termsQuery.refetch()}
              disabled={termsQuery.isFetching}
            >
              Retry
            </Button>
          </div>
        )}
        {termsQuery.isSuccess && termsQuery.data?.agreement && (
          <OrganizationTermsAgreement
            // Consent belongs to this exact organization and agreement revision.
            // Remounting also isolates in-flight mutations when organizations change.
            key={JSON.stringify([
              organizationId,
              termsQuery.data.agreement.id,
              termsQuery.data.agreement.version,
              termsQuery.data.agreement.url,
            ])}
            status={termsQuery.data}
            agreement={termsQuery.data.agreement}
          />
        )}
        {onContinue &&
          termsQuery.isSuccess &&
          !termsQuery.isFetching &&
          canContinue && (
            <div className="space-y-2 border-t pt-4">
              <Button variant="outline" className="w-full" onClick={onContinue}>
                Continue to organization
              </Button>
              <p className="text-sm text-muted-foreground">
                You can review these terms again in Settings.
              </p>
            </div>
          )}
      </CardContent>
    </Card>
  );
}
